/**
 * Modal that asks for the parent password and resolves the calling flow
 * once it succeeds. Used in two places:
 *
 *  1. Long-press on Pumpkin → re-prompt to enter the parent area
 *     (defensive layer; the device cookie is already authenticated).
 *  2. First-launch "Set up Eva's spelling words" CTA → same prompt, just
 *     entered through a different door.
 *
 * It re-runs `POST /api/login` to verify the password. On 200 the cookie
 * is refreshed (harmlessly), and `onSuccess` fires. On 401 the user gets
 * a gentle "didn't work, try again". Network/server errors map to their
 * usual copy.
 *
 * The cookie this issues is the same device session cookie. There's no
 * separate "parent session" concept — parent mode is purely a
 * client-side in-memory flag (see spec §3.2).
 */
import { useState } from "react";
import type { FormEvent } from "react";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";
import "./PasswordPrompt.css";

export interface PasswordPromptProps {
  open: boolean;
  title: string;
  description?: string;
  submitLabel?: string;
  onSuccess: () => void;
  onClose: () => void;
}

export function PasswordPrompt({
  open,
  title,
  description,
  submitLabel = "Enter",
  onSuccess,
  onClose,
}: PasswordPromptProps) {
  const { login } = useAuth();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    const result = await login(password);
    setSubmitting(false);
    if (result.ok) {
      setPassword("");
      onSuccess();
      return;
    }
    if (result.reason === "invalid_password") {
      setError("That didn't match. Try again.");
      setPassword("");
    } else if (result.reason === "network") {
      setError("Couldn't reach the server. Check the connection.");
    } else if (result.reason === "server_misconfigured") {
      setError("The server isn't set up right yet.");
    } else {
      setError("Something went wrong. Try again.");
    }
  }

  function handleClose() {
    setPassword("");
    setError(null);
    onClose();
  }

  return (
    <Modal open={open} onClose={handleClose} titleId="password-prompt-title">
      <h2 id="password-prompt-title" className="password-prompt__title">
        {title}
      </h2>
      {description !== undefined && (
        <p className="password-prompt__description">{description}</p>
      )}
      <form className="password-prompt__form" onSubmit={handleSubmit} noValidate>
        <label className="password-prompt__label" htmlFor="parent-password">
          Password
        </label>
        <input
          id="parent-password"
          className="password-prompt__input"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={submitting}
          autoFocus
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
        {error !== null && (
          <p className="password-prompt__error" role="alert">
            {error}
          </p>
        )}
        <div className="password-prompt__actions">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting || password.length === 0}>
            {submitting ? "Checking…" : submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
